import React, { useEffect, useMemo, useState } from 'react';
import { useGetDashboardQuery, useUpdateDashboardPreferencesMutation } from '../../../../../redux/services/TraderApi';
import {
  type TraderDashboardMetric,
  type TTraderDashboardCurrency,
  type TTraderDashboardPreferences,
} from '../../../../../redux/types/TraderTypes';
import styles from './TraderBalanceBlock.module.css';

const metricOptions: Array<{ key: TraderDashboardMetric; label: string }> = [
  { key: 'balance', label: 'Эквивалент баланса' },
  { key: 'received', label: 'Получено' },
  { key: 'frozen', label: 'Заморожено' },
  { key: 'requisites', label: 'Реквизиты' },
];

const formatAmount = (value: string | null, scale = 2) => {
  if (value === null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: scale,
    minimumFractionDigits: 0,
  }).format(Number(value));
};

const formatMoney = (value: string | null, code: string, scale = 2) => {
  if (value === null) return '—';
  return `${formatAmount(value, scale)} ${code}`;
};

const clonePreferences = (preferences: TTraderDashboardPreferences) => ({
  show_currency_block: preferences.show_currency_block !== false,
  currency_codes: [...preferences.currency_codes],
  metrics: { ...preferences.metrics },
});

const CurrencyCard = ({
  currency,
  metrics,
}: {
  currency: TTraderDashboardCurrency;
  metrics: TTraderDashboardPreferences['metrics'];
}) => (
  <article className={styles.currencyCard}>
    <div className={styles.currencyHeader}>
      <div>
        <div className={styles.currencyCode}>{currency.code}</div>
        <div className={styles.currencyName}>{currency.name}</div>
      </div>
      <span className={currency.active_requisites ? styles.activeBadge : styles.mutedBadge}>
        {currency.active_requisites ? 'Активна' : 'Нет активных'}
      </span>
    </div>
    <div className={styles.metricGrid}>
      {metrics.balance && (
        <div className={styles.metric}>
          <span>Эквивалент баланса</span>
          <strong>{formatMoney(currency.balance_equivalent, currency.code, currency.amount_scale)}</strong>
          {currency.rate && <small>Курс: {formatAmount(currency.rate, 4)}</small>}
        </div>
      )}
      {metrics.received && (
        <div className={styles.metric}>
          <span>Получено</span>
          <strong>{formatMoney(currency.received, currency.code, currency.amount_scale)}</strong>
        </div>
      )}
      {metrics.frozen && (
        <div className={styles.metric}>
          <span>Заморожено</span>
          <strong>{formatMoney(currency.frozen, currency.code, currency.amount_scale)}</strong>
          <small>{formatMoney(currency.frozen_usdt, 'USDT', 3)} в работе</small>
        </div>
      )}
      {metrics.requisites && (
        <div className={styles.metric}>
          <span>Реквизиты</span>
          <strong>{currency.active_requisites} активных</strong>
          <small>{currency.requisites} всего</small>
        </div>
      )}
    </div>
    {!currency.active_orders && <div className={styles.cardFooter}>Нет заявок в работе</div>}
    {!!currency.active_orders && (
      <div className={styles.cardFooter}>Заявок в работе: {currency.active_orders}</div>
    )}
  </article>
);

export const TraderBalanceBlock = () => {
  const { data, isLoading, isFetching, isError, refetch } = useGetDashboardQuery(undefined, {
    pollingInterval: 10000,
  });
  const [updatePreferences, { isLoading: isSaving }] = useUpdateDashboardPreferencesMutation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState<TTraderDashboardPreferences | null>(null);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (settingsOpen && data?.preferences) setDraft(clonePreferences(data.preferences));
  }, [settingsOpen, data?.preferences]);

  useEffect(() => {
    if (!settingsOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSettingsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [settingsOpen]);

  const visibleCurrencies = useMemo(() => {
    if (!data) return [];
    const selected = new Set(data.preferences.currency_codes);
    return data.currencies.filter((currency) => selected.has(currency.code));
  }, [data]);

  const openSettings = () => {
    if (data?.preferences) setDraft(clonePreferences(data.preferences));
    setSaveError('');
    setSettingsOpen(true);
  };

  const toggleCurrency = (code: string) => {
    if (!draft) return;
    const selected = draft.currency_codes.includes(code)
      ? draft.currency_codes.filter((item) => item !== code)
      : [...draft.currency_codes, code];
    setDraft({ ...draft, currency_codes: selected });
  };

  const toggleMetric = (metric: TraderDashboardMetric) => {
    if (!draft) return;
    setDraft({ ...draft, metrics: { ...draft.metrics, [metric]: !draft.metrics[metric] } });
  };

  const saveSettings = async () => {
    if (!draft || !draft.currency_codes.length || !Object.values(draft.metrics).some(Boolean)) return;
    try {
      await updatePreferences(draft).unwrap();
      setSettingsOpen(false);
    } catch {
      setSaveError('Не удалось сохранить настройки. Попробуйте ещё раз.');
    }
  };

  if (isLoading) {
    return <div className={styles.state}>Загружаем балансы…</div>;
  }

  if (isError || !data) {
    return (
      <div className={styles.errorState} role='alert'>
        <div>
          <strong>Не удалось загрузить балансы</strong>
          <span>Проверьте соединение и повторите попытку.</span>
        </div>
        <button type='button' onClick={refetch}>Повторить</button>
      </div>
    );
  }

  const showCurrencyBlock = data.preferences.show_currency_block !== false;

  return (
    <section className={styles.shell} aria-label='Балансы трейдера'>
      <div className={styles.heading}>
        <div>
          <div className={styles.eyebrow}>ПЛАТЁЖНЫЙ БАЛАНС</div>
          <h2>Средства</h2>
          <p>Баланс в USDT и результаты по каждой подключённой валюте</p>
        </div>
        <div className={styles.headingActions}>
          {isFetching && <span className={styles.syncing}>Обновляем…</span>}
          <button
            type='button'
            className={styles.settingsButton}
            onClick={openSettings}
            aria-expanded={settingsOpen}
            aria-haspopup='dialog'
          >
            <span aria-hidden='true'>⚙</span> Настроить вид
          </button>
        </div>
      </div>

      <div className={styles.summary}>
        <div className={`${styles.summaryCard} ${styles.primarySummary}`}>
          <span>Доступно</span>
          <strong>{formatMoney(data.balance_usdt, data.base_currency, 3)}</strong>
          <small>Единый расчётный баланс</small>
        </div>
        <div className={styles.summaryCard}>
          <span>Заморожено</span>
          <strong>{formatMoney(data.frozen_usdt, data.base_currency, 3)}</strong>
          <small>Только активные заявки</small>
        </div>
        <div className={styles.summaryCard}>
          <span>Заявки в работе</span>
          <strong>{data.active_orders}</strong>
          <small>По всем валютам</small>
        </div>
        <div className={styles.summaryCard}>
          <span>Активные реквизиты</span>
          <strong>{data.active_requisites}</strong>
          <small>Готовы принимать заявки</small>
        </div>
      </div>

      {showCurrencyBlock && (
        <div className={styles.currencySection}>
          <div className={styles.currencyTitle}>
            <div>
              <h3>Валюты</h3>
              <span>Фактические суммы заявок не смешиваются между валютами</span>
            </div>
            <span className={styles.currencyCount}>{visibleCurrencies.length} подключено</span>
          </div>

          {visibleCurrencies.length ? (
            <div className={styles.currencyGrid}>
              {visibleCurrencies.map((currency) => (
                <CurrencyCard key={currency.code} currency={currency} metrics={data.preferences.metrics} />
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>Выберите хотя бы одну валюту в настройках отображения.</div>
          )}
        </div>
      )}

      {settingsOpen && draft && (
        <div className={styles.overlay} role='presentation' onMouseDown={() => setSettingsOpen(false)}>
          <div
            className={styles.dialog}
            role='dialog'
            aria-modal='true'
            aria-labelledby='balance-settings-title'
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className={styles.dialogHeader}>
              <div>
                <span className={styles.eyebrow}>ПЕРСОНАЛИЗАЦИЯ</span>
                <h3 id='balance-settings-title'>Настройте свой экран</h3>
              </div>
              <button type='button' className={styles.closeButton} onClick={() => setSettingsOpen(false)} aria-label='Закрыть'>×</button>
            </div>
            <fieldset>
              <legend>Разделы</legend>
              <label className={`${styles.option} ${styles.blockOption}`}>
                <input
                  type='checkbox'
                  checked={draft.show_currency_block}
                  onChange={() => setDraft({
                    ...draft,
                    show_currency_block: !draft.show_currency_block,
                  })}
                />
                <span>
                  <strong>Показывать блок «Валюты»</strong>
                  <small>Заголовок и карточки валют можно скрыть целиком</small>
                </span>
              </label>
            </fieldset>
            <fieldset>
              <legend>Валюты</legend>
              <div className={styles.optionGrid}>
                {data.currencies.map((currency) => (
                  <label key={currency.code} className={styles.option}>
                    <input
                      type='checkbox'
                      checked={draft.currency_codes.includes(currency.code)}
                      onChange={() => toggleCurrency(currency.code)}
                    />
                    <span><strong>{currency.code}</strong><small>{currency.name}</small></span>
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend>Показатели</legend>
              <div className={styles.optionGrid}>
                {metricOptions.map((metric) => (
                  <label key={metric.key} className={styles.option}>
                    <input
                      type='checkbox'
                      checked={draft.metrics[metric.key]}
                      onChange={() => toggleMetric(metric.key)}
                    />
                    <span>{metric.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className={styles.dialogFooter}>
              <span>{saveError || (draft.show_currency_block
                ? 'Нельзя скрыть все валюты или показатели'
                : 'Выбранные валюты сохранятся, блок будет скрыт')}</span>
              <div>
                <button type='button' className={styles.cancelButton} onClick={() => setSettingsOpen(false)}>Отмена</button>
                <button
                  type='button'
                  className={styles.saveButton}
                  onClick={saveSettings}
                  disabled={isSaving || !draft.currency_codes.length || !Object.values(draft.metrics).some(Boolean)}
                >
                  {isSaving ? 'Сохраняем…' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
